'use client';

import { PropsWithChildren, useCallback } from 'react';

import { NextIntlClientProvider as OriginalNextIntlClientProvider } from 'next-intl';
import { useLocale } from 'next-intl';
import { useEvent } from 'react-use';

import { SYNC_EVENTS } from '@/lib/sync/events';
import { useSyncEvent } from '@/lib/sync/hooks/use-sync-event';
import { changeLocale } from '@/lib/utils/locale';

interface NextIntlClientProviderProps extends PropsWithChildren {
  locale: string;
  messages: Record<string, unknown>;
  timeZone: string;
}

function LocaleSyncLogic() {
  const locale = useLocale();

  const handleLocaleChange = useCallback(
    (newLocale: string) => {
      if (newLocale !== locale) {
        changeLocale(newLocale);
      }
    },
    [locale],
  );

  useSyncEvent(SYNC_EVENTS.LOCALE, handleLocaleChange);

  useEvent('message', (event: MessageEvent) => {
    if (event.data.type === 'setLocale' && event.data.locale !== locale) {
      changeLocale(event.data.locale);
    }
  });

  return null;
}

export function NextIntlClientProvider({
  children,
  locale,
  messages,
  timeZone,
}: NextIntlClientProviderProps) {
  return (
    <OriginalNextIntlClientProvider locale={locale} messages={messages} timeZone={timeZone}>
      <LocaleSyncLogic />
      {children}
    </OriginalNextIntlClientProvider>
  );
}
